import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import useTranslation from "../hooks/useTranslation";
import { useStore } from "../contexts/StoreContext";
import { isTauriMode } from "../../utils/environmentDetector";
import { checkStoreLocation } from "../../utils/desktop/locationControl";

const CHECK_INTERVAL_MS = 5 * 60 * 1000;

export default function LocationControlGate() {
  const translation = useTranslation();
  const { currentStore } = useStore();
  const [blocked, setBlocked] = useState(null);
  const [checking, setChecking] = useState(false);

  const verify = async () => {
    if (!isTauriMode()) {
      setBlocked(null);
      return;
    }
    setChecking(true);
    try {
      const result = await checkStoreLocation(currentStore);
      if (result.status === "outside") {
        setBlocked({
          kind: "outside",
          distanceMeters: result.distanceMeters,
          radiusMeters: result.radiusMeters,
        });
      } else if (result.status === "unavailable") {
        setBlocked({ kind: "unavailable" });
      } else {
        setBlocked(null);
      }
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    verify();
    const timer = setInterval(verify, CHECK_INTERVAL_MS);
    return () => clearInterval(timer);
    // currentStore fields are the inputs that change the rule.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentStore?.id,
    currentStore?.locationControlEnabled,
    currentStore?.locationLatitude,
    currentStore?.locationLongitude,
    currentStore?.locationRadiusMeters,
  ]);

  const description =
    blocked?.kind === "outside"
      ? `${translation.locationOutsideArea || "This station is outside the authorized area."} ${translation.locationMeasuredDistance || "Measured distance"}: ${blocked.distanceMeters} m. ${translation.locationRadiusMeters || "Authorized radius (meters)"}: ${blocked.radiusMeters} m.`
      : translation.locationUnavailable ||
        "Location is unavailable. This station cannot be verified.";

  return (
    <AlertDialog open={!!blocked}>
      <AlertDialogContent
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>
            {translation.locationControlTitle || "Location control"}
          </AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button className="min-h-12 min-w-12" disabled={checking} onClick={verify}>
            {translation.locationCheckAgain || "Check location again"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
